import { Vector3, type Web3DViewer } from '@technology/web3d';
import { IconsPlugin, type PointIcon } from '@technology/web3d-plugin-icons';
import { getLogger } from '@trimble/logging';
import { arePointsEqual, type Point3D } from '@trimble/utilities';
import {
  CapabilityCoordinatorBase,
  type CapabilityRegistry,
} from '@trimble/web3d';
import { batch, createEffect, createRoot, createSignal } from 'solid-js';
import { configReady, getMarkerIconPath, iconsVersion } from '../constants';
import { resolveMarkerHashId } from '../utils';
import {
  type ApplyInput,
  type IssueMarkerIconCoordinatorOptions,
  type MarkerInfo,
  type PreviewMarkerVisuals,
} from './issue-marker-icon-coordinator.types';
import { defaultPaneEligibility, getEligibleViewers } from './pane-eligibility';

const PREVIEW_MARKER_ICON_ID = -1;
const MARKER_ICON_SIZE = 32;
/** Max distance (metres) to treat a persisted marker as replacing the preview. */
const PREVIEW_MATCH_EPSILON = 1e-3;

/**
 * Manages issue marker icons across eligible Web3D viewers:
 * a single preview marker during placement and the full list of persisted
 * topic markers.
 */
export class IssueMarkerIconCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private readonly isPaneEligible: NonNullable<
    IssueMarkerIconCoordinatorOptions['isPaneEligible']
  >;

  private readonly getActiveMarkerPoint: () => Readonly<Point3D> | null;

  private readonly updateActiveMarkerPoint: (
    point: Readonly<Point3D> | null
  ) => void;

  private readonly updatePreviewMarkerVisuals: (
    visuals: PreviewMarkerVisuals | null
  ) => void;

  private readonly updateListMarkers: (markers: readonly MarkerInfo[]) => void;

  private readonly updateSelectedMarkerId: (id: string | null) => void;

  private readonly iconOperationChains = new WeakMap<
    IconsPlugin,
    Promise<void>
  >();

  private readonly ownedIcons = new WeakMap<IconsPlugin, PointIcon[]>();

  private readonly pendingJobs = new Map<IconsPlugin, number>();

  /** Plugins that still own icons or have queued work. Strong refs until remove succeeds. */
  private readonly trackedPlugins = new Set<IconsPlugin>();

  private previousPlugins: IconsPlugin[] = [];
  private pendingApply: ApplyInput | undefined;
  private applyScheduled = false;
  private applyGeneration = 0;
  private disposeRoot: (() => void) | undefined;

  constructor(
    registry: CapabilityRegistry,
    options: IssueMarkerIconCoordinatorOptions = {}
  ) {
    super(registry);
    this.isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;

    const [activeMarkerPoint, updateActiveMarkerPoint] =
      createSignal<Readonly<Point3D> | null>(null);
    this.getActiveMarkerPoint = activeMarkerPoint;
    this.updateActiveMarkerPoint = updateActiveMarkerPoint;

    const [previewMarkerVisuals, updatePreviewMarkerVisuals] =
      createSignal<PreviewMarkerVisuals | null>(null);
    this.updatePreviewMarkerVisuals = updatePreviewMarkerVisuals;

    const [listMarkers, updateListMarkers] = createSignal<
      readonly MarkerInfo[]
    >([]);
    this.updateListMarkers = updateListMarkers;

    const [selectedMarkerId, updateSelectedMarkerId] = createSignal<
      string | null
    >(null);
    this.updateSelectedMarkerId = updateSelectedMarkerId;

    this.disposeRoot = createRoot((dispose) => {
      createEffect(() => {
        const ready = configReady();
        void iconsVersion();
        const previewPoint = activeMarkerPoint();
        const previewVisuals = previewMarkerVisuals();
        const markers = listMarkers();
        const selectedId = selectedMarkerId();
        const viewers = this.getEligibleViewers();

        this.scheduleApply({
          previewPoint,
          previewVisuals,
          markers: ready ? markers : [],
          selectedId,
          viewers,
        });
      });

      return dispose;
    });
  }

  public showMarker(
    point: Readonly<Point3D>,
    visuals?: { markerType?: string; priority?: string }
  ): void {
    batch(() => {
      this.updatePreviewMarkerVisuals(visuals ?? null);
      this.updateActiveMarkerPoint(point);
    });
  }

  public removeMarker(): void {
    batch(() => {
      this.updateActiveMarkerPoint(null);
      this.updatePreviewMarkerVisuals(null);
    });
  }

  /**
   * @intent Clear active preview when persisted marker reaches the same point,
   * then update list markers so preview and persisted icons do not coexist.
   */
  public setMarkers(markers: readonly MarkerInfo[]): void {
    batch(() => {
      const preview = this.getActiveMarkerPoint();
      if (
        preview &&
        markers.some((marker) =>
          arePointsEqual(marker.point, preview, PREVIEW_MATCH_EPSILON)
        )
      ) {
        this.removeMarker();
      }
      this.updateListMarkers(markers);
    });
  }

  public setSelectedMarkerId(id: string | null): void {
    this.updateSelectedMarkerId(id);
  }

  public async cleanup(): Promise<void> {
    this.applyGeneration += 1;
    const generation = this.applyGeneration;
    this.pendingApply = undefined;

    this.disposeRoot?.();
    this.disposeRoot = undefined;

    const plugins = this.collectKnownPlugins();
    for (const plugin of plugins) {
      this.enqueuePluginJob(plugin, [], generation);
    }

    await Promise.all(
      plugins
        .map((plugin) => this.iconOperationChains.get(plugin))
        .filter((promise): promise is Promise<void> => promise !== undefined)
    );

    this.previousPlugins = [];
  }

  private getEligibleViewers(): Web3DViewer[] {
    return getEligibleViewers(this.registry, this.isPaneEligible);
  }

  /**
   * @intent Every plugin that may still own icons: one we still track after a
   * failed remove, one with queued work, one from the last apply, or one whose
   * pane is eligible before the first apply.
   */
  private collectKnownPlugins(): IconsPlugin[] {
    const plugins = [...this.trackedPlugins];
    const seen = new Set(plugins);

    for (const plugin of [
      ...this.pendingJobs.keys(),
      ...this.previousPlugins,
      ...pluginsOf(this.getEligibleViewers()),
    ]) {
      if (seen.has(plugin)) {
        continue;
      }
      seen.add(plugin);
      plugins.push(plugin);
    }

    return plugins;
  }

  private scheduleApply(input: ApplyInput): void {
    this.pendingApply = input;
    if (this.applyScheduled) {
      return;
    }

    this.applyScheduled = true;
    queueMicrotask(() => {
      this.applyScheduled = false;
      const next = this.pendingApply;
      this.pendingApply = undefined;
      if (next) {
        void this.applyDesiredState(next);
      }
    });
  }

  private async applyDesiredState(input: ApplyInput): Promise<void> {
    const generation = ++this.applyGeneration;

    try {
      const desired = await this.buildDesiredIcons(input, generation);
      if (desired === undefined) {
        return;
      }
      if (generation !== this.applyGeneration) {
        return;
      }

      const currentPlugins = pluginsOf(input.viewers);
      const current = new Set(currentPlugins);
      const stalePlugins = this.previousPlugins.filter(
        (plugin) => !current.has(plugin)
      );
      this.previousPlugins = currentPlugins;

      for (const plugin of currentPlugins) {
        this.enqueuePluginJob(plugin, desired, generation);
      }
      for (const plugin of stalePlugins) {
        this.enqueuePluginJob(plugin, [], generation);
      }
    } catch (error: unknown) {
      getLogger().warn('Failed to apply issue marker icons', {
        error,
        phase: 'build',
      });
    }
  }

  private async buildDesiredIcons(
    input: ApplyInput,
    generation: number
  ): Promise<PointIcon[] | undefined> {
    const desired: PointIcon[] = [];
    const previewPoint = input.previewPoint;

    if (previewPoint) {
      const iconPath = await getMarkerIconPath({
        markerType: input.previewVisuals?.markerType,
        priority: input.previewVisuals?.priority,
        isPending: true,
      });
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      desired.push({
        id: PREVIEW_MARKER_ICON_ID,
        iconPath,
        position: new Vector3(previewPoint.x, previewPoint.y, previewPoint.z),
        size: MARKER_ICON_SIZE,
      });
    }

    const hashMap = new Map<number, string>();
    for (const marker of input.markers) {
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      const hashId = resolveMarkerHashId(marker.issueId, hashMap);
      if (hashId === -1) {
        continue;
      }
      hashMap.set(hashId, marker.issueId);
      const iconPath = await getMarkerIconPath({
        markerType: marker.type,
        priority: marker.priority,
        isSelected: marker.issueId === input.selectedId,
      });
      if (generation !== this.applyGeneration) {
        return undefined;
      }
      desired.push({
        id: hashId,
        iconPath,
        position: new Vector3(marker.point.x, marker.point.y, marker.point.z),
        size: MARKER_ICON_SIZE,
      });
    }

    return desired;
  }

  private enqueuePluginJob(
    plugin: IconsPlugin,
    desired: PointIcon[],
    generation: number
  ): void {
    this.trackedPlugins.add(plugin);
    this.pendingJobs.set(plugin, (this.pendingJobs.get(plugin) ?? 0) + 1);

    const previous = this.iconOperationChains.get(plugin) ?? Promise.resolve();
    const next = previous
      .then(async () => {
        // Empty desired must still run. A newer apply on other plugins
        // must not cancel a stale-plugin remove.
        if (desired.length > 0 && generation !== this.applyGeneration) {
          return;
        }

        const previousOwned = this.ownedIcons.get(plugin) ?? [];
        if (previousOwned.length > 0) {
          await plugin.remove(previousOwned);
          this.ownedIcons.set(plugin, []);
        }

        if (desired.length > 0 && generation !== this.applyGeneration) {
          return;
        }

        if (desired.length > 0) {
          await plugin.add(desired);
          this.ownedIcons.set(plugin, desired);
        }
      })
      .catch((error: unknown) => {
        getLogger().warn('Failed to apply issue marker icons', {
          error,
          phase: 'plugin',
        });
      })
      .finally(() => {
        const remaining = (this.pendingJobs.get(plugin) ?? 1) - 1;
        if (remaining > 0) {
          this.pendingJobs.set(plugin, remaining);
        } else {
          this.pendingJobs.delete(plugin);
          const leftover = this.ownedIcons.get(plugin);
          if (!leftover || leftover.length === 0) {
            this.trackedPlugins.delete(plugin);
          }
        }
      });
    this.iconOperationChains.set(plugin, next);
  }
}

function pluginsOf(viewers: readonly Web3DViewer[]): IconsPlugin[] {
  const plugins: IconsPlugin[] = [];
  const seen = new Set<IconsPlugin>();

  for (const viewer of viewers) {
    const plugin = viewer.plugins.get(IconsPlugin);
    if (!plugin || seen.has(plugin)) {
      continue;
    }
    seen.add(plugin);
    plugins.push(plugin);
  }

  return plugins;
}
